import React, { useState, useEffect } from "react";
import "./DynamicEducationTable.css";

interface EducationEntry {
  startDate: string;
  endDate: string;
  fieldOfStudy: string;
  degreeName: string;
  institutionName: string;
  institutionAddress: string;
  degreeObtained: string;
}

interface EducationData {
  hasEducation: string; // 'Yes' or 'No'
  educationYes: string;
  educationNo: string;
  [key: string]: any; // For numbered entries like study1, study2
}

interface DynamicEducationTableProps {
  value: EducationData;
  onChange: (value: EducationData) => void;
  required?: boolean;
}

const DynamicEducationTable: React.FC<DynamicEducationTableProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const [educationData, setEducationData] = useState<EducationData>(
    value || {
      hasEducation: "No",
      educationYes: "",
      educationNo: "N",
    }
  );

  useEffect(() => {
    setEducationData(
      value || {
        hasEducation: "No",
        educationYes: "",
        educationNo: "N",
      }
    );
  }, [value]);

  // Add keyboard support for Ctrl+Enter to add new entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleHasEducationChange = (hasEducation: string) => {
    let updatedData: EducationData = {
      ...educationData,
      hasEducation,
      educationYes: hasEducation === "Yes" ? "Y" : "",
      educationNo: hasEducation === "No" ? "N" : "",
    };

    // Clear education entries if "No" is selected
    if (hasEducation === "No") {
      Object.keys(updatedData).forEach((key) => {
        if (key.startsWith("study")) {
          delete (updatedData as any)[key];
        }
      });
    }

    setEducationData(updatedData);
    onChange(updatedData);
  };

  const addEntry = () => {
    // Only allow one entry - use fixed key "study1"
    const newEntry: EducationEntry = {
      startDate: "",
      endDate: "",
      fieldOfStudy: "",
      degreeName: "",
      institutionName: "",
      institutionAddress: "",
      degreeObtained: "No",
    };

    const updatedData = {
      ...educationData,
      study1: newEntry,
    };
    setEducationData(updatedData);
    onChange(updatedData);
  };

  const removeEntry = (key: string) => {
    // Clear the single entry instead of removing it
    const clearedData: EducationData = {
      hasEducation: educationData.hasEducation,
      educationYes: educationData.educationYes,
      educationNo: educationData.educationNo,
    };

    setEducationData(clearedData);
    onChange(clearedData);
  };

  const updateEntry = (
    key: string,
    field: keyof EducationEntry,
    value: string
  ) => {
    const updatedData = {
      ...educationData,
      [key]: { ...educationData[key], [field]: value },
    };
    setEducationData(updatedData);
    onChange(updatedData);
  };

  const validateDate = (dateString: string): boolean => {
    if (!dateString) return true; // Empty dates are valid for optional fields

    const dateRegex = /^\d{4}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    const year = parseInt(dateString.split("-")[0]);
    const month = parseInt(dateString.split("-")[1]);

    if (year < 1900 || year > new Date().getFullYear() + 10) return false;
    if (month < 1 || month > 12) return false;

    return true;
  };

  return (
    <div className="dynamic-education-table">
      {/* Education Question Section */}
      <div className="education-question">
        <h4>Have you had any post secondary(12th) education</h4>
        <div className="education-options">
          <label className="radio-option">
            <input
              type="radio"
              name="hasEducation"
              value="Yes"
              checked={educationData.hasEducation === "Yes"}
              onChange={(e) => handleHasEducationChange(e.target.value)}
            />
            <span>Yes</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="hasEducation"
              value="No"
              checked={educationData.hasEducation === "No"}
              onChange={(e) => handleHasEducationChange(e.target.value)}
            />
            <span>No</span>
          </label>
        </div>
      </div>
      {/* Education Details Section - Only show if "Yes" is selected */}
      {educationData.hasEducation === "Yes" && (
        <div className="education-details">
          <div className="table-header">
            <h4>Education After 12th</h4>
            <p className="table-description">
              Please enter your highest level of post-secondary education details below.
            </p>
          </div>

          {Object.keys(educationData).filter((key) => key.startsWith("study"))
            .length === 0 ? (
            <div className="empty-state">
              <p>No education entries added yet.</p>
              <button
                type="button"
                className="add-first-btn"
                onClick={addEntry}
              >
                Add Education Details
              </button>
            </div>
          ) : (
            <div className="entries-container">
              {Object.keys(educationData)
                .filter((key) => key.startsWith("study"))
                .map((key, index) => (
                  <div key={key} className="entry-card">
                    <div className="entry-header">
                      <div className="entry-number">
                        <span className="entry-badge">📚</span>
                        <h5>Education Details</h5>
                      </div>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeEntry(key)}
                        title="Clear education details"
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="entry-form">
                      <div className="form-row form-row-1">
                        <div className="field-group">
                          <label>Study Started (yyyy-mm)</label>
                          <input
                            type="text"
                            value={educationData[key].startDate}
                            onChange={(e) =>
                              updateEntry(key, "startDate", e.target.value)
                            }
                            placeholder="2020-09"
                            className={
                              !validateDate(educationData[key].startDate) &&
                              educationData[key].startDate
                                ? "invalid"
                                : ""
                            }
                          />
                          {!validateDate(educationData[key].startDate) &&
                            educationData[key].startDate && (
                              <small className="error-text">
                                Invalid format (yyyy-mm)
                              </small>
                            )}
                        </div>
                        
                        <div className="field-group">
                          <label>Study End (yyyy-mm)</label>
                          <input
                            type="text"
                            value={educationData[key].endDate}
                            onChange={(e) =>
                              updateEntry(key, "endDate", e.target.value)
                            }
                            placeholder="2024-05"
                            className={
                              !validateDate(educationData[key].endDate) &&
                              educationData[key].endDate
                                ? "invalid"
                                : ""
                            }
                          />
                          {!validateDate(educationData[key].endDate) &&
                            educationData[key].endDate && (
                              <small className="error-text">
                                Invalid format (yyyy-mm)
                              </small>
                            )}
                        </div>
                        
                        <div className="field-group">
                          <label>Field of Study</label>
                          <input
                            type="text"
                            value={educationData[key].fieldOfStudy}
                            onChange={(e) =>
                              updateEntry(key, "fieldOfStudy", e.target.value)
                            }
                            placeholder="e.g., Computer Science"
                          />
                        </div>
                      </div>
                      
                      <div className="form-row form-row-2">
                        <div className="field-group">
                          <label>Name of Degree</label>
                          <input
                            type="text"
                            value={educationData[key].degreeName}
                            onChange={(e) =>
                              updateEntry(key, "degreeName", e.target.value)
                            }
                            placeholder="e.g., Bachelor of Science"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>Institution Name</label>
                          <input
                            type="text"
                            value={educationData[key].institutionName}
                            onChange={(e) =>
                              updateEntry(
                                key,
                                "institutionName",
                                e.target.value
                              )
                            }
                            placeholder="e.g., MIT"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>City/Town</label>
                          <input
                            type="text"
                            value={educationData[key].institutionAddress}
                            onChange={(e) =>
                              updateEntry(
                                key,
                                "institutionAddress",
                                e.target.value
                              )
                            }
                            placeholder="City/Town of Institution"
                          />
                        </div>
                      </div>
                      
                      <div className="form-row form-row-3">
                        <div className="field-group">
                          <label>Did you get the degree?</label>
                          <select
                            value={educationData[key].degreeObtained}
                            onChange={(e) =>
                              updateEntry(key, "degreeObtained", e.target.value)
                            }
                          >
                            <option value="No">No</option>
                            <option value="Yes">Yes</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

            </div>
          )}

          {required &&
            Object.keys(educationData).filter((key) => key.startsWith("study"))
              .length === 0 && (
              <small className="error-text">
                At least one education entry is required
              </small>
            )}
        </div>
      )}
    </div>
  );
};

export default DynamicEducationTable;
